/**
 * Mirror of the API's client-inquiry RECORD (`client-inquiries`,
 * packages/api/src/services/organization/client-inquiries/client-inquiry-record.ts).
 * plan/2026-10-01_FROM-GANTRY_client-inquiries-contract.md.
 *
 * Not to be confused with `ClientInquiries/` beside this folder: that screen
 * reads the ANSWERS client instances file (`client-inquiry-answers`). This
 * one is the QUESTION, before it reaches the client.
 */

export type InquiryStage = "internal" | "released" | "answered" | "withdrawn";

/**
 * How a released question reaches the client (the API's RELEASE_CHANNELS,
 * client-inquiry-record.ts): into the client's IntraSync app — which HQ does
 * itself as part of the Release tap since 2026-11-02 (Decision 5,
 * `delivery: "publish"`) — or as the PDF the person sends.
 */
export type ReleaseChannel = "instance" | "pdf";

export const RELEASE_CHANNEL_LABEL: Record<ReleaseChannel, string> = {
  instance: "In their IntraSync app",
  pdf: "As the PDF",
};

/**
 * What a client's IntraSync app takes: four options, A to D. That is Module-Core's
 * rule; the API mirrors it as INSTANCE_MAX_OPTIONS and refuses a five-option
 * release to the app (422 `instance-option-limit`). This copy only greys the
 * choice out before the person tries: the API is the gate.
 */
export const INSTANCE_MAX_OPTIONS = 4;

/** Why a question with this many options cannot go into the client's app, or null when it can. The API's sentence. */
export const instanceOptionRefusal = (optionCount: number): string | null => {
  if (optionCount <= INSTANCE_MAX_OPTIONS) return null;
  const last = String.fromCharCode("A".charCodeAt(0) + Math.min(optionCount, 5) - 1);
  return (
    `This question has ${optionCount} options (A to ${last}). A client's IntraSync app takes at most ` +
    `${INSTANCE_MAX_OPTIONS} (A to D) until Module-Core widens its inquiry form, so it cannot go into their app. ` +
    "Send it as the PDF instead."
  );
};

export interface InquiryOption {
  id: string;
  letter: string;
  title: string;
  text: string | null;
  implication: string | null;
}

export interface PersonRef {
  userId: number | null;
  email: string | null;
  name: string | null;
}

export interface InquiryAnswer {
  id: number;
  side: "internal" | "client";
  optionIds: string[];
  letters: string[];
  text: string | null;
  author: PersonRef;
  /** [hub] H3: the answering person's email, flat (author.email). */
  byEmail?: string | null;
  via: "hq-web" | "gantry" | "client-instance" | "recorded" | string;
  recordedBy: PersonRef | null;
  /** [hub] review round 1: an operator/admin answered over this assignee (null: the assignee answered). */
  overrideOf?: PersonRef | null;
  clientAnswerId: number | null;
  answeredFor: Record<string, unknown> | null;
  answeredAt: string;
  receivedAt: string;
}

export interface ClientText {
  kind: "design" | "data";
  title: string;
  intro: string;
  framing?: string | null;
  optionText: Record<string, string>;
  recommendation?: string | null;
  respondBy?: string | null;
}

export interface InquiryRecord {
  id: number;
  /** `id` null: a project or internal meeting's question, with no client to send it to ([hub] H3). */
  client: { id: string | null; code: string | null; name: string | null };
  question: string;
  normalisedQuestion: string;
  questionKey: string;
  /** The reference to publish under; minted at filing, unguessable. */
  externalRef: string | null;
  multi: boolean;
  options: InquiryOption[];
  /** INTERNAL ONLY — never published. */
  internalContext: string | null;
  /** What the client reads; null until a person writes it. */
  clientText: ClientText | null;
  origins: Array<Record<string, unknown> & { kind: string; attachedAt: string; detachedAt?: string }>;
  stage: InquiryStage;
  stageAt: string;
  filedAt: string;
  filedBy: PersonRef & { via: string; login: string | null };
  answers: InquiryAnswer[];
  releasedBy: (PersonRef & { via: string | null; claimed: Record<string, unknown> | null }) | null;
  releasedAt: string | null;
  /**
   * `delivery`: "published" when HQ put it into the client's app and the app said it
   * holds it (Decision 5); "manual" when a person said they sent it. `channel`: how
   * it reached the client; null (or absent) when nobody said.
   */
  release: {
    externalRef: string | null;
    delivery: string | null;
    channel?: ReleaseChannel | null;
    instanceId: null;
    detail: string | null;
  } | null;
  withdrawnBy: (PersonRef & { via: string | null; claimed: Record<string, unknown> | null }) | null;
  withdrawnAt: string | null;
  withdrawReason: string | null;
  orphaned: boolean;
  nudgeDueAt: string | null;
  /** [hub] H3: who must answer it — the owner of the work it blocks. */
  assigneeUserId?: number | null;
  assigneeEmail?: string | null;
  /**
   * [hub] H3/H4: the action items it NAMES — the only work it holds out of their
   * owner's queue until answered (operator ruling A, 2026-11-02).
   */
  blocksActionItemIds?: number[];
  /**
   * [hub] H4b: the meeting(s) it was filed against. Since ruling A a link that
   * holds nothing: the meeting's open work shows the question as a flag.
   */
  blocksMeetingId?: number | null;
  blocksMeetingIds?: number[];
  /**
   * Operator ruling B (2026-11-02): one of its meetings is private, so only the
   * people who were in it, its assignee and administrators see it, and only
   * someone from the meeting may be asked to answer it.
   */
  privateMeeting?: boolean;
  updatedAt: string;
}

/** Ruling B: why a question from a private meeting is narrowed, as the dialog says it. */
export const PRIVATE_QUESTION_NOTE =
  "From a private meeting: only the people who were in it, the person it is asked of, and administrators can see this question. Only someone who was in the meeting can be asked to answer it.";

/**
 * Ruling A: the line about the meeting(s) a question was filed against — a
 * link that holds none of their work. Null when it names no meeting.
 */
export const meetingLinkLine = (r: Pick<InquiryRecord, "blocksMeetingIds" | "blocksMeetingId">): string | null => {
  const ids = r.blocksMeetingIds?.length ? r.blocksMeetingIds : r.blocksMeetingId != null ? [r.blocksMeetingId] : [];
  if (!ids.length) return null;
  const which = ids.length === 1 ? `meeting #${ids[0]}` : `meetings ${ids.map((id) => `#${id}`).join(", ")}`;
  return `Filed against ${which}: ${ids.length === 1 ? "its" : "their"} open work shows this question, and it holds none of that work. A question holds only the items it names.`;
};

/** The assign form's words: who is told, what stays held — and, for a private meeting's question, who may be asked. */
export const assignNote = (r: Pick<InquiryRecord, "privateMeeting">): string =>
  "The new assignee is told. The items this question names stay held until it is answered; nothing else moves." +
  (r.privateMeeting ? " It comes from a private meeting, so ask someone who was in that meeting — HQ refuses anyone else." : "");

export interface InquiryPage {
  total: number;
  limit: number;
  skip: number;
  data: InquiryRecord[];
  serverNow: string;
  settleMs: number;
  /** Whether the signed-in person may release — a hint from the server's own policy. */
  mayRelease: boolean;
  /** [hub] review round 1: a person with operator/admin access — may re-assign, and answer over an assignee. A hint. */
  mayAssign?: boolean;
}

/** The header's stage filter. "open" = internal + released (the default). */
export type InquiryStageFilter = "open" | InquiryStage | "all";

export const INQUIRY_STAGE_OPTIONS: Array<{ value: InquiryStageFilter; label: string }> = [
  { value: "open", label: "Open" },
  { value: "internal", label: "Internal" },
  { value: "released", label: "Released" },
  { value: "answered", label: "Answered" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "all", label: "All" },
];

/** The API's `stage` query for a filter, or undefined for every stage. */
export const stageQuery = (filter: InquiryStageFilter): string | undefined => {
  if (filter === "all") return undefined;
  if (filter === "open") return "internal,released";
  return filter;
};

export const STAGE_LABEL: Record<InquiryStage, string> = {
  internal: "Internal",
  released: "Released to client",
  answered: "Answered",
  withdrawn: "Withdrawn",
};

export const STAGE_COLOR: Record<InquiryStage, "warning" | "info" | "success" | "default"> = {
  internal: "warning",
  released: "info",
  answered: "success",
  withdrawn: "default",
};

/** The actions the dialog offers. Each mirrors a server rule, so no button is offered that the API refuses. */
export type InquiryAction = "answer-internal" | "answer-client" | "release" | "client-text" | "withdraw" | "assign";

/**
 * [hub] review round 1 — who is looking. Only the question's ASSIGNEE answers,
 * or someone with operator/admin access in their place (recorded as an
 * override); only operator/admin access re-assigns. Absent: the older,
 * stage-only answer (kept for callers that do not know the viewer).
 */
export interface InquiryViewer {
  userId: number | null;
  mayAssign: boolean;
}

/**
 * Which actions a record offers, mirroring the API's stage rules:
 *   internal  → answer internally, release (a person on the release list),
 *               write the client's text (#4211: saved before release, so its PDF
 *               and email are made from words a person wrote), withdraw
 *   released  → record the client's answer, answer internally, withdraw (release list only)
 *   answered / withdrawn → nothing; they are terminal.
 * Release and the client's text are withheld for a multi-select question, which
 * the API refuses to release (`multi-unsupported`) until the client's answer form
 * is confirmed to take one, and for a question with no client to send it to.
 */
export const legalActions = (
  record: Pick<InquiryRecord, "stage" | "multi"> & { client?: { id: string | null }; assigneeUserId?: number | null },
  mayRelease: boolean,
  viewer?: InquiryViewer,
): InquiryAction[] => {
  // [hub] H3: a question with no client has nobody to be released to (API: 422 `no-client`).
  const hasClient = record.client === undefined || record.client.id !== null;
  const mayAnswer =
    !viewer ||
    viewer.mayAssign ||
    (viewer.userId !== null && record.assigneeUserId != null && Number(record.assigneeUserId) === viewer.userId);
  const assign = viewer?.mayAssign ? (["assign"] as const) : [];
  if (record.stage === "internal") {
    return [
      ...(mayAnswer ? (["answer-internal"] as const) : []),
      ...(mayRelease && !record.multi && hasClient ? (["release"] as const) : []),
      ...(!record.multi && hasClient ? (["client-text"] as const) : []),
      "withdraw",
      ...assign,
    ];
  }
  if (record.stage === "released") {
    return [
      ...(mayAnswer ? (["answer-client", "answer-internal"] as const) : []),
      ...(mayRelease ? (["withdraw"] as const) : []),
      ...assign,
    ];
  }
  return [];
};

/** The client column: a client-less question says what it is. */
export const clientLabel = (r: Pick<InquiryRecord, "client">): string =>
  r.client.name ?? r.client.code ?? (r.client.id === null ? "No client (project or internal)" : "Client");

/** Open stages first, then newest change — the order "My questions" reads in. */
export const openFirst = (rows: InquiryRecord[]): InquiryRecord[] => {
  const open = (r: InquiryRecord) => (r.stage === "internal" || r.stage === "released" ? 0 : 1);
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => open(a.r) - open(b.r) || a.i - b.i)
    .map((x) => x.r);
};

export const personLabel = (p: PersonRef | null | undefined): string => {
  if (!p) return "—";
  return p.name || p.email || (p.userId != null ? `User ${p.userId}` : "—");
};

/** How an answer reads: who decided, and — for a client — how it reached HQ. */
export const answerByline = (a: InquiryAnswer): string => {
  if (a.side === "client" && a.via === "recorded") {
    return `The client (${personLabel(a.author)}) — recorded by ${personLabel(a.recordedBy)}`;
  }
  if (a.side === "client") return `The client (${personLabel(a.author)}), from their own instance`;
  const via = a.via === "gantry" ? " through Gantry" : "";
  const over = a.overrideOf ? ` (answering for ${personLabel(a.overrideOf)})` : "";
  return `${personLabel(a.author)}${via}${over}`;
};

export const originLabel = (o: InquiryRecord["origins"][number]): string => {
  switch (o.kind) {
    case "lift":
      return `Gantry lift ${String(o.liftId ?? "")}${o.box ? ` on ${String(o.box)}` : ""}`;
    case "workItem":
      return `Work item ${String(o.id ?? "")}`;
    case "ticket":
      return `Ticket ${String(o.handle ?? o.ticketId ?? "")}`;
    case "hq":
      return "Filed in HQ";
    default:
      return o.kind;
  }
};

/** HQ itself put this question into the client's IntraSync app (and the app said it holds it). */
export const publishedIntoApp = (r: Pick<InquiryRecord, "release">): boolean => r.release?.delivery === "published";

/**
 * The reference the client's instance must answer under, so its answer joins
 * this record. The API MINTS it when the question is filed
 * (`hq-inquiry-<id>-<nonce>`, `externalRefFor` in client-inquiry-record.ts) —
 * unguessable, so never computed here; the release form shows it BEFORE
 * release, because the person sends the question first and taps Release after.
 */
export const externalRefOf = (r: Pick<InquiryRecord, "externalRef" | "release">): string | null =>
  r.externalRef ?? r.release?.externalRef ?? null;

export const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/** "3 days", "5 hours" — how long the record has sat in its stage. */
export const ageOf = (iso: string, now: number = Date.now()): string => {
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return "under an hour";
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  return `${days} days`;
};

/** An empty client text for the release form, seeded from what the record already holds. */
export const clientTextDraft = (r: InquiryRecord): ClientText => ({
  kind: r.clientText?.kind ?? "design",
  title: r.clientText?.title ?? "",
  intro: r.clientText?.intro ?? "",
  framing: r.clientText?.framing ?? null,
  optionText: Object.fromEntries(r.options.map((o) => [o.id, r.clientText?.optionText?.[o.id] ?? o.text ?? ""])),
  recommendation: r.clientText?.recommendation ?? null,
  respondBy: r.clientText?.respondBy ?? null,
});

/** What the release form still lacks — the same paths the API's 422 `not-releasable` lists. */
export const missingForRelease = (t: ClientText, options: InquiryOption[]): string[] => {
  const missing: string[] = [];
  if (!t.title.trim()) missing.push("title");
  if (!t.intro.trim()) missing.push("intro");
  for (const o of options) if (!(t.optionText[o.id] ?? "").trim()) missing.push(`option ${o.letter}`);
  return missing;
};
